<template>
	<div class="tasks-app">
		<!-- Unauthenticated: render only nested route (login) -->
		<router-view v-if="!user" />

		<!-- Authenticated shell -->
		<div v-else class="t-shell">
			<aside class="t-sidebar">
				<div class="t-logo">
					<span class="dot"></span>
					<span>TaskFlow</span>
				</div>

				<div class="t-section-title">תפריט</div>
				<nav class="t-nav">
					<router-link to="/tasks" class="t-nav-item" exact-active-class="active">
						<span class="t-icon">📊</span>
						<span>לוח בקרה</span>
					</router-link>
					<router-link to="/tasks/projects" class="t-nav-item" active-class="active">
						<span class="t-icon">📁</span>
						<span>פרויקטים</span>
					</router-link>
					<router-link to="/tasks/all" class="t-nav-item" active-class="active">
						<span class="t-icon">✅</span>
						<span>כל המשימות</span>
					</router-link>
					<router-link to="/tasks/mine" class="t-nav-item" active-class="active">
						<span class="t-icon">⭐</span>
						<span>המשימות שלי</span>
					</router-link>
				</nav>

				<div class="t-section-title">בזמן אמת</div>
				<div style="padding: 0 12px;">
					<span class="t-live"><span class="t-live-dot"></span> מחובר</span>
				</div>

				<div class="t-user-card">
					<div class="t-avatar" :style="{background: user.color || '#6366f1'}">{{ initials(user.name) }}</div>
					<div class="t-meta">
						<span class="t-name">{{ user.name }}</span>
						<span class="t-mail">{{ user.email }}</span>
					</div>
					<span class="t-logout" title="התנתקות" @click="doLogout">↩</span>
				</div>
			</aside>

			<main class="t-main">
				<div class="t-topbar">
					<div class="t-topbar-spacer"></div>
					<NotificationsBell :user="user" @open-task="onOpenTaskFromNotif" />
				</div>
				<router-view v-slot="{ Component }">
					<keep-alive :max="6">
						<component
							:is="Component"
							:user="user"
							:users="users"
							:projects-list="projects"
							:open-task-id="pendingOpenTaskId"
							@users-changed="loadUsers"
							@projects-changed="loadProjects"
							@task-opened="pendingOpenTaskId = 0"
						/>
					</keep-alive>
				</router-view>
			</main>
		</div>
	</div>
</template>

<script>
import { TasksAPI, TasksRealtime, initials } from "./TasksApi";
import NotificationsBell from "./NotificationsBell.vue";
import "../../styles/tasks/tasks.scss";

export default {
	name: "TasksApp",
	components: { NotificationsBell },
	data() {
		return {
			user: null,
			users: [],
			projects: [],
			pendingOpenTaskId: 0,
		};
	},
	async created() {
		await this.bootstrap();
		this.askBrowserPerm();
	},
	beforeUnmount() {
		TasksRealtime.disconnect();
	},
	methods: {
		initials,
		isPublicRoute() {
			return this.$route.path === "/tasks/login" || this.$route.path === "/tasks/reset";
		},
		async bootstrap() {
			const token = TasksAPI.getToken();
			if (!token) {
				if (!this.isPublicRoute()) this.$router.replace("/tasks/login");
				return;
			}
			const cached = TasksAPI.getUser();
			if (cached) this.user = cached;
			// Run me + users + projects + Pusher init in parallel for faster first paint
			const [res] = await Promise.all([
				TasksAPI.me(),
				this.loadUsers(),
				this.loadProjects(),
				TasksRealtime.ensure().catch(() => {}),
			]);
			if (res && res.user) {
				this.user = res.user;
				TasksAPI.setUser(res.user);
			} else {
				TasksAPI.clear();
				this.user = null;
				if (!this.isPublicRoute()) this.$router.replace("/tasks/login");
			}
		},
		async loadUsers() {
			const res = await TasksAPI.users();
			this.users = (res && res.items) || [];
		},
		async loadProjects() {
			const res = await TasksAPI.projects();
			this.projects = (res && res.items) || [];
		},
		async doLogout() {
			await TasksAPI.logout();
			TasksAPI.clear();
			TasksRealtime.disconnect();
			this.user = null;
			this.$router.replace("/tasks/login");
		},
		onOpenTaskFromNotif(id) {
			this.pendingOpenTaskId = +id || 0;
		},
		askBrowserPerm() {
			try {
				if (window.Notification && Notification.permission === "default") {
					Notification.requestPermission().catch(() => {});
				}
			} catch (_) {}
		},
	},
	watch: {
		"$route"() {
			// when navigating to login while authed, redirect away
			if (this.user && this.$route.path === "/tasks/login") {
				this.$router.replace("/tasks");
			}
			if (!this.user && !this.isPublicRoute()) {
				this.bootstrap();
			}
		},
	},
};
</script>
